import React, { FC, memo, Suspense, useLayoutEffect } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';

import { useChannelProgramInfoQuery } from '@/scenarios/legacy-player/hooks/useChannelProgramInfoQuery';

import { useChannelProgramsGroupedByDay } from '../../hooks/use-channel-programs-grouped-by-day';
import { useLastCompletedChannelProgramId } from '../../hooks/use-last-completed-channel-program-id';
import { useChannelProgramErrorRaised } from '../../analytics/hooks/useChannelProgramErrorRaised';
import { ProgramLoaderComponent } from '../program-loader/component';
import { LoadingProgramErrorComponent } from '../loading-program-error/component';
import ChannelProgramModalHeaderContainer from '../../../../containers/ChannelProgramModalHeader/ChannelProgramModalHeader';

import styles from './styles.module.css';
import { ChannelProgramModal } from './component';

export interface ChannelProgramModalContainerProps {
    contentId: string;
    isOpened: boolean;
    onShow?: () => void;
    onClose?: () => void;
    onTrackNavigated?: () => void;
}

const ChannelProgramModalComponent: FC<ChannelProgramModalContainerProps> = memo(
    ({ contentId, isOpened, onShow, onClose, onTrackNavigated }) => {
        const onRequestFailed = useChannelProgramErrorRaised({ channelId: contentId });

        const { programs } = useChannelProgramInfoQuery({
            contentId,
            onRequestFailed,
        });

        const programsGroupedByDate = useChannelProgramsGroupedByDay(programs ?? undefined);
        const lastCompletedChannelProgramId = useLastCompletedChannelProgramId(
            programs ?? undefined
        );

        useLayoutEffect(() => {
            if (isOpened && contentId) {
                onShow?.();
                onTrackNavigated?.();

                return () => onClose?.();
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [contentId, isOpened]);

        if (!isOpened) return null;

        return (
            <ChannelProgramModal
                programsGroupedByDate={programsGroupedByDate}
                firstVisibleProgramId={lastCompletedChannelProgramId}
            />
        );
    }
);

export const ChannelProgramModalContainer = (props: ChannelProgramModalContainerProps) => {
    const { contentId } = props;

    return (
        <div className={styles.root}>
            <ChannelProgramModalHeaderContainer channelId={contentId} />
            <ErrorBoundary fallback={LoadingProgramErrorComponent}>
                <Suspense fallback={<ProgramLoaderComponent />}>
                    <ChannelProgramModalComponent {...props} />
                </Suspense>
            </ErrorBoundary>
        </div>
    );
};
